import { Period } from '../../../../enums/reports';
import { useGetOccupancyQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import {
    Card,
    CardContent,
    Typography,
    lighten,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { capitalizeName } from '../../../../../shared/utils';

interface WeeklyChartProps {
    period: Period;
    module: number;
}

const WeeklyChart = ({ period, module }: WeeklyChartProps) => {
    const theme = useTheme();
    const { data } = useGetOccupancyQuery({
        period,
        module,
    });

    type Data = { fill: string; day: string; count: number };

    const [transformedData, setTransformedData] = useState<Data[]>();

    const getGradientColor = (
        value: number,
        min: number,
        max: number,
    ): string => {
        if (max === min) return lighten(theme.palette.primary.main, 0.5); // fallback

        const ratio = (value - min) / (max - min);
        const lightness = 0.7 - ratio * 0.5;

        return lighten(theme.palette.primary.main, lightness);
    };

    useEffect(() => {
        if (!data) return;

        const entries = Object.entries(data[0].weeklyOccupancy).map(
            ([day, count]) => ({
                day,
                count: Number(count),
            }),
        );

        const counts = entries.map((d) => d.count);
        const min = Math.min(...counts);
        const max = Math.max(...counts);

        const converted = entries.map((entry) => ({
            ...entry,
            fill: getGradientColor(entry.count, min, max),
        }));

        setTransformedData(converted);
    }, [data]);

    if (!data) return <></>;

    return (
        <>
            <Card>
                <CardContent>
                    <Typography variant='subHeading'>
                        Weekly Occupancy
                    </Typography>
                    <Typography variant='subtitle2' gutterBottom>
                        Nights occupied
                    </Typography>

                    <ResponsiveContainer width='100%' height={300}>
                        <BarChart data={transformedData}>
                            <CartesianGrid strokeDasharray='3 3' />
                            <XAxis
                                dataKey='day'
                                tickFormatter={(value) =>
                                    `${value.charAt(0).toUpperCase()}`
                                }
                            />
                            <YAxis />
                            <Tooltip
                                labelFormatter={(value) =>
                                    capitalizeName(value)
                                }
                                formatter={(value: number) =>
                                    `${value} night/s`
                                }
                            />
                            {/* <Legend /> */}
                            <Bar dataKey='count' />
                        </BarChart>
                    </ResponsiveContainer>
                    <Typography variant='caption'>
                        Displays the number of occupied nights for each day. Use
                        this to help identify peak times during each week.
                    </Typography>
                </CardContent>
            </Card>
        </>
    );
};

export default WeeklyChart;
